// src/views/Hospital-Admin/Dashboard/dashboard.js
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";
import { getPatientsAll } from "../../../Services/patientService";
import {
  ensurePatientHasVisit,
  formatPatientName,
  calculateAge,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import { resolveHospitalName } from "../../../utils/printEngine";

// Native HMIS SVG Icons
import Search from "../../../assets/images/comman/search-outline.svg";
import PatientsIcon from "../../../assets/images/comman/sidebar/patients.svg";
import OPDIcon from "../../../assets/images/comman/sidebar/OPD.svg";
import PharmacyIcon from "../../../assets/images/comman/sidebar/Pharmacy.svg";
import BillingIcon from "../../../assets/images/comman/sidebar/Billing.svg";
import LabIcon from "../../../assets/images/comman/sidebar/AdmissionsIPD.svg";
import ClinicalIcon from "../../../assets/images/comman/sidebar/ClinicalServices.svg";
import AssessmentIcon from "../../../assets/images/OPD-patients/Assessment.svg";
import ViewIcon from "../../../assets/images/OPD-patients/view.svg";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, can, canAny, hasRole } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [navigatingId, setNavigatingId] = useState(null);

  const roleName = user?.role_name || "Healthcare Staff";
  const userName = user?.name || "User";
  const hospitalName = resolveHospitalName(user?.hospital_name);

  // Fetch live patients for queue preview & real metrics
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPatientsAll({ limit: 20 });
      const rawList = res?.data?.patients || res?.patients || [];
      if (Array.isArray(rawList)) {
        const transformed = rawList.map((p) => {
          const name =
            p.name && typeof p.name === "string" && p.name.trim() && p.name.trim() !== "undefined undefined"
              ? p.name.trim()
              : p.fullName && typeof p.fullName === "string" && p.fullName.trim() && p.fullName.trim() !== "undefined undefined"
              ? p.fullName.trim()
              : formatPatientName(p) || "Patient";

          const dobFormatted = formatDobDisplay(p.dob || p.dateOfBirth);
          const computedAge = calculateAge(dobFormatted) || p.age || "";

          const visitTimestamp =
            p.lastVisitDate ||
            p.time ||
            p.appointmentDate ||
            p.updatedAt ||
            p.lastVisit ||
            p.createdAt;

          const dateStr = visitTimestamp ? new Date(visitTimestamp).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }) : "Today";

          const timeStr = visitTimestamp ? new Date(visitTimestamp).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }) : "—";

          return {
            id: p._id || p.id,
            _id: p._id || p.id,
            name,
            age: computedAge ? `${computedAge}yr` : "",
            dob: dobFormatted,
            mobile: p.mobile || p.phone || "",
            opdId: p.opdId || (p._id || p.id || "").slice(-8).toUpperCase(),
            uhid: p.uhid || p.UHID || (p._id || p.id || "").slice(-8).toUpperCase(),
            queue: p.queue != null ? String(p.queue).padStart(2, "0") : "—",
            doctor: p.doctor || p.doctorName || "Consulting Physician",
            visitTime: timeStr,
            visitDate: dateStr,
            rawData: p,
          };
        });
        setPatients(transformed);
      } else {
        setPatients([]);
      }
    } catch (err) {
      console.warn("Failed to fetch dashboard patient queue:", err);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Navigate to consultation on patient click
  const handlePatientClick = async (patient) => {
    const pId = patient.id || patient._id;
    if (!pId || navigatingId) return;

    try {
      setNavigatingId(pId);
      const patientToUse = await ensurePatientHasVisit(patient.rawData || patient);
      const effectiveId = patientToUse.id || pId;
      navigate(`/OPDDetails?patientId=${encodeURIComponent(effectiveId)}`, {
        state: {
          patientData: patientToUse.rawData || patient.rawData || patient,
          patientId: effectiveId,
        },
      });
    } catch (err) {
      console.error("Navigation error:", err);
      navigate(`/OPDDetails?patientId=${encodeURIComponent(pId)}`);
    } finally {
      setNavigatingId(null);
    }
  };

  // Workspace tiles authorized for current role
  const WORKSPACE_MODULES = [
    {
      title: "OPD Queue",
      desc: "Manage outpatient queue, consultation status, and vitals",
      path: "/patients",
      icon: OPDIcon,
      show: can(PERMISSIONS.OPD_VIEW_QUEUE),
    },
    {
      title: "Patients Directory",
      desc: "Register new patients, view profiles, and link ABHA IDs",
      path: "/patients-list",
      icon: PatientsIcon,
      show: can(PERMISSIONS.PATIENTS_READ),
    },
    {
      title: "Pharmacy & Dispensing",
      desc: "Review active prescriptions and dispense medications",
      path: "/pharmacy",
      icon: PharmacyIcon,
      show:
        hasRole(1, 2, 7) &&
        canAny(
          PERMISSIONS.PHARMACY_VIEW_ORDERS,
          PERMISSIONS.PHARMACY_DISPENSE,
          PERMISSIONS.CLINICAL_PRESCRIPTION_READ
        ),
    },
    {
      title: "Billing & Invoices",
      desc: "Generate patient bills, record payments, and view accounts",
      path: "/billing",
      icon: BillingIcon,
      show:
        hasRole(1, 2, 8, 9, 11) &&
        canAny(
          PERMISSIONS.BILLING_READ,
          PERMISSIONS.BILLING_READ_INVOICE,
          PERMISSIONS.BILLING_CREATE_INVOICE
        ),
    },
    {
      title: "Clinical Assessment",
      desc: "Triage patient complaints, vitals, and nursing assessments",
      path: "/select-patient-assessment",
      icon: AssessmentIcon,
      show:
        hasRole(1, 3, 5, 10, 11) &&
        canAny(
          PERMISSIONS.CLINICAL_ASSESSMENT_READ,
          PERMISSIONS.CLINICAL_ASSESSMENT_WRITE
        ),
    },
    {
      title: "Lab & Diagnostics",
      desc: "Process pathology test orders, enter parameters, and reports",
      path: "/create-lab-report",
      icon: LabIcon,
      show:
        hasRole(1, 2, 3, 6, 10, 11) &&
        canAny(
          PERMISSIONS.LAB_READ,
          PERMISSIONS.LAB_ORDER,
          PERMISSIONS.LAB_ENTER_RESULTS
        ),
    },
    {
      title: "Discharge Summary",
      desc: "Create and finalize inpatient discharge care documentation",
      path: "/discharge-summary",
      icon: ClinicalIcon,
      show:
        hasRole(3, 5, 10, 11) &&
        canAny(
          PERMISSIONS.CLINICAL_DISCHARGE_READ,
          PERMISSIONS.CLINICAL_DISCHARGE_WRITE
        ),
    },
    {
      title: "Facility Management",
      desc: "Configure hospital departments, operational units, and staff",
      path: "/hospital-details",
      icon: ClinicalIcon,
      show: hasRole(1, 2) && can(PERMISSIONS.HOSPITAL_MANAGE),
    },
  ];

  const visibleWorkspaces = WORKSPACE_MODULES.filter((w) => w.show);

  // Filtered patients for queue
  const filteredPatients = patients.filter((p) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (p.name && p.name.toLowerCase().includes(q)) ||
      (p.uhid && p.uhid.toLowerCase().includes(q)) ||
      (p.opdId && p.opdId.toLowerCase().includes(q)) ||
      (p.doctor && p.doctor.toLowerCase().includes(q)) ||
      (p.mobile && p.mobile.includes(q))
    );
  });

  return (
    <>
      <style>
        {`
          .patient-row-hover:hover {
            background-color: #f8f9fa !important;
            transition: background-color 0.2s ease;
          }
          .patient-row-hover:hover td {
            background-color: inherit !important;
          }
          .dashboard-stat-card {
            background: #ffffff;
            border: 1px solid #E5E7EB;
            border-radius: 12px;
            padding: 18px 20px;
            transition: all 0.2s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          }
          .dashboard-stat-card:hover {
            border-color: #CBD5E1;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
            transform: translateY(-2px);
          }
          .dashboard-module-card {
            background: #ffffff;
            border: 1px solid #E5E7EB;
            border-radius: 12px;
            padding: 16px 20px;
            transition: all 0.2s ease;
            text-decoration: none;
            color: inherit;
            display: flex;
            align-items: center;
            gap: 16px;
          }
          .dashboard-module-card:hover {
            border-color: #1C64F2;
            box-shadow: 0 4px 12px rgba(28, 100, 242, 0.08);
            transform: translateY(-2px);
            color: inherit;
          }
        `}
      </style>

      <div className="container-fluid">
        <div className="opd-page">
          {/* Header Section matching HMIS Design System */}
          <div className="header-section-new">
            <div className="header-left">
              <ul className="nav nav-tabs-new">
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Overview" ? "active" : ""}`}
                    onClick={() => setActiveTab("Overview")}
                  >
                    Clinical Overview
                  </span>
                </li>
                {/* <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Queue" ? "active" : ""}`}
                    onClick={() => setActiveTab("Queue")}
                  >
                    Today's Patient Queue ({patients.length})
                  </span>
                </li> */}
              </ul>
            </div>

            <div className="header-right">
              <div className="search-bar-new">
                <span className="search-icon">
                  <img src={Search} alt="Search" />
                </span>
                <input
                  type="text"
                  placeholder="Search patients, UHID, OPD ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                className="btn-refresh-new"
                onClick={fetchDashboardData}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "↻ Refresh"}
              </button>

              {can(PERMISSIONS.PATIENTS_REGISTER) && (
                <Link to="/add-patient" className="btn-add-new">
                  + Add Patient
                </Link>
              )}
            </div>
          </div>

          {/* Clean Hospital & User Greeting Bar */}
          <div
            className="d-flex align-items-center justify-content-between p-3 mb-4 bg-white rounded-3 border"
            style={{ borderColor: "#E5E7EB" }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, color: "#1F2A37", fontSize: "18px" }}>
                Welcome, {userName}
              </h4>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span style={{ fontSize: "13px", color: "#6B7280" }}>
                {hospitalName}
                </span>
                <span
                  style={{
                    backgroundColor: "#EBF5FF",
                    color: "#1C64F2",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "3px 10px",
                    borderRadius: "16px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {roleName}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <span
                style={{
                  fontSize: "13px",
                  color: "#4B5563",
                  backgroundColor: "#F9FAFB",
                  padding: "6px 14px",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  fontWeight: 500,
                }}
              >
                {new Date().toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          {/* Operational Stat KPI Cards */}
          {/* <div className="row g-3 mb-4">
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="dashboard-stat-card">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#6B7280" }}>
                    Today's OPD Queue
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: "#EBF5FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={OPDIcon} alt="OPD" style={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1F2A37" }}>
                  {loading ? "—" : patients.length}
                </div>
                <div style={{ fontSize: "12px", color: "#10B981", fontWeight: 500, marginTop: "4px" }}>
                  ● Active Consultations
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="dashboard-stat-card">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#6B7280" }}>
                    Registered Patients
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: "#F0FDF4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={PatientsIcon} alt="Patients" style={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1F2A37" }}>
                  {loading ? "—" : patients.length > 0 ? `${patients.length * 4}+` : "0"}
                </div>
                <div style={{ fontSize: "12px", color: "#6B7280", fontWeight: 500, marginTop: "4px" }}>
                  UHID & ABHA Linked Records
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="dashboard-stat-card">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#6B7280" }}>
                    Pharmacy Orders
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: "#FEF3C7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={PharmacyIcon} alt="Pharmacy" style={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1F2A37" }}>
                  {loading ? "—" : Math.max(0, Math.floor(patients.length * 0.7))}
                </div>
                <div style={{ fontSize: "12px", color: "#D97706", fontWeight: 500, marginTop: "4px" }}>
                  ● Prescriptions in Dispense Queue
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="dashboard-stat-card">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#6B7280" }}>
                    Diagnostic Lab Reports
                  </span>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: "#F3E8FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={LabIcon} alt="Lab" style={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1F2A37" }}>
                  {loading ? "—" : Math.max(0, Math.floor(patients.length * 0.4))}
                </div>
                <div style={{ fontSize: "12px", color: "#7C3AED", fontWeight: 500, marginTop: "4px" }}>
                  Tests Awaiting Finalization
                </div>
              </div>
            </div>
          </div> */}

          {/* Quick Workspaces Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 style={{ margin: 0, fontWeight: 700, color: "#1F2A37", fontSize: "15px" }}>
                Authorized Clinical & Administrative Workspaces
              </h5>
            </div>

            <div className="row g-3">
              {visibleWorkspaces.map((module, idx) => (
                <div key={idx} className="col-12 col-sm-6 col-lg-3">
                  <Link to={module.path} className="dashboard-module-card">
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "10px",
                        backgroundColor: "#F3F4F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={module.icon}
                        alt={module.title}
                        style={{ width: "22px", height: "22px" }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#1F2A37",
                          marginBottom: "2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {module.title}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6B7280",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {module.desc}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Live Patient Queue Section */}
          <div className="OpdBodySection">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h5 style={{ margin: 0, fontWeight: 700, color: "#1F2A37", fontSize: "15px" }}>
                  Recent Patient Activity & Queue
                </h5>
                <small style={{ color: "#6B7280", fontSize: "12px" }}>
                  Select any patient row to open their clinical consultation and medical record.
                </small>
              </div>

              {can(PERMISSIONS.OPD_VIEW_QUEUE) && (
                <Link
                  to="/patients"
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#1C64F2",
                    textDecoration: "none",
                  }}
                >
                  View Full OPD Queue →
                </Link>
              )}
            </div>

            <div className="opdAllTable">
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>OPD ID</th>
                    <th>Queue</th>
                    <th>UHID</th>
                    <th>Time</th>
                    <th>Doctor</th>
                    <th style={{ textAlign: "center", width: "120px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 mb-0 text-muted">Loading hospital queue...</p>
                      </td>
                    </tr>
                  ) : filteredPatients.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="alert alert-info mb-0">
                          <strong>No active patient records found.</strong>
                          <br />
                          <small className="text-muted">
                            {searchTerm
                              ? "No patients matched your search query."
                              : "New patient consultations will appear here automatically."}
                          </small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredPatients.slice(0, 10).map((patient, index) => {
                      const pId = patient.id || patient._id;
                      return (
                        <tr
                          key={pId || index}
                          style={{
                            cursor: navigatingId === pId ? "wait" : "pointer",
                            opacity: navigatingId === pId ? 0.7 : 1,
                          }}
                          onClick={() => handlePatientClick(patient)}
                          className="patient-row-hover"
                          title="Click to view consultation details"
                        >
                          <td>
                            <div className="patientDetails">
                              <h5>{patient.name}</h5>
                              <p>
                                {patient.age ? `Age: ${patient.age}` : ""}
                                {patient.dob ? ` | DOB: ${patient.dob}` : ""}
                              </p>
                              {patient.mobile && (
                                <p className="text-muted small">
                                  Mobile: {patient.mobile}
                                </p>
                              )}
                              {navigatingId === pId && (
                                <p className="text-primary small mb-0 mt-1">
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Opening consultation…
                                </p>
                              )}
                            </div>
                          </td>

                          <td>
                            <h5>{patient.opdId}</h5>
                          </td>

                          <td>
                            <h5>{patient.queue}</h5>
                          </td>

                          <td>
                            <h5 style={{ fontFamily: "monospace" }}>{patient.uhid}</h5>
                          </td>

                          <td>
                            <div className="patientTime">
                              <h5>{patient.visitTime}</h5>
                              <p>{patient.visitDate}</p>
                            </div>
                          </td>

                          <td>
                            <h5>{patient.doctor}</h5>
                          </td>

                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex align-items-center justify-content-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  padding: "4px 10px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  borderRadius: "6px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                                onClick={() => handlePatientClick(patient)}
                              >
                                <img
                                  src={ViewIcon}
                                  alt="View"
                                  style={{ width: "14px", height: "14px" }}
                                />
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;